import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Card, Typography } from '@mui/material'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDownloadWithPolicy } from '../../common/DownloadPolicyDialog'
import { saveBlob, filenameFromResponse } from '../freight-profitability/freightPnlUtils'
import EmptyState from '../merchants/shared/EmptyState'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { KIND, download, errorText, fetchTatGrid } from './ptlSetupApi'
import { ROUTES, fmtDate, num } from './constants'
import { Banner, Breadcrumb } from './components/SetupUi'
import PtlPage from './shell/PtlPage'
import CustomLanes, { customLanes } from './steps/CustomLanes'
import TatMatrix from './tat/TatMatrix'

/**
 * PTL FREIGHT → PTL Merchant Setup → Courier TAT → one courier (route /tat/courier/lanes?courier=). Its own lanes (P)
 * against the Prozo standard, the effective matrix (standard on the lanes it has not given), the merchants on it,
 * download, and the Upload / History screens (screens/targets 'courier-tat').
 */
export default function PtlTatCourierLanes() {
  const [params] = useSearchParams()
  const courier = params.get('courier') || ''
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { triggerDownload, PolicyDialog } = useDownloadWithPolicy()
  const [grid, setGrid] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    if (!courier) return
    setError('')
    fetchTatGrid(null, courier).then(setGrid).catch((e) => setError(errorText(e, 'Could not load the courier TAT')))
  }, [courier])
  useEffect(() => { setGrid(null); load() }, [load])

  const crumb = <Breadcrumb parent='Courier TAT' onParent={() => navigate(ROUTES.tatCouriers)} current={courier || 'Courier'} />
  const lanes = customLanes(grid, 'P')
  const faster = lanes.filter((l) => l.diff.tone === 'faster').length
  const slower = lanes.filter((l) => l.diff.tone === 'slower').length
  const usedBy = grid?.usedBy || []
  const latest = grid?.latest

  const doDownload = async () => {
    try {
      const res = await download(KIND.TAT, null, courier)
      saveBlob(res.data, filenameFromResponse(res, `ptl_courier_tat_${courier}.csv`))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Download failed'), { variant: 'error' })
    }
  }

  const kpis = [
    { key: 'lanes', label: 'Courier lanes', value: grid ? `${num(grid.courierLanes)} / ${num(grid.cells.length)}` : '', sub: grid ? `${num(grid.standardLanes)} follow the standard` : '' },
    { key: 'faster', label: 'Faster than standard', value: grid ? num(faster) : '', sub: 'Courier MAX below standard MAX' },
    { key: 'slower', label: 'Slower than standard', value: grid ? num(slower) : '', sub: 'Courier MAX above standard MAX' },
    { key: 'merchants', label: 'Merchants on it', value: grid ? num(usedBy.length) : '', sub: 'Put on it in onboarding Step 2' },
    { key: 'last', label: 'Last change', value: latest ? fmtDate(latest.createdAt).split(',')[0] : 'None',
      sub: latest ? latest.createdBy || '' : 'Not uploaded yet' },
  ]

  if (!courier) {
    return (
      <PtlPage title='Courier TAT' breadcrumb={crumb}>
        <EmptyState title='No courier picked' body='Open a courier from Merchant TAT → Courier TAT.'
          action={<Button variant='contained' onClick={() => navigate(ROUTES.tatCouriers)} sx={SX.cta}>Back to Courier TAT</Button>} />
      </PtlPage>
    )
  }

  return (
    <PtlPage title={`TAT · ${courier}`} breadcrumb={crumb}
      description={`Lanes with a ${courier} TAT (P) use it for every merchant on this courier’s TAT; every other lane follows the Prozo standard (S). A merchant’s own custom lanes still win over both.`}
      actions={(
        <>
          <Button variant='outlined' startIcon={<DownloadOutlinedIcon />} disabled={!grid} onClick={() => triggerDownload(doDownload, `${courier} TAT`)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
            Download TAT
          </Button>
          <Button variant='outlined' startIcon={<HistoryOutlinedIcon />} onClick={() => navigate(ROUTES.tatCourierHistory(courier))} sx={{ ...SX.ctaGhost, minHeight: 36 }}>History</Button>
          <Button variant='contained' startIcon={<UploadFileOutlinedIcon />} disabled={!grid || !grid.cells.length} onClick={() => navigate(ROUTES.tatCourierUpload(courier))}
            sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>
            {grid?.courierLanes ? 'Update TAT' : 'Upload TAT'}
          </Button>
        </>
      )}
      kpis={kpis} kpisLoading={!grid && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}

      {grid && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Card elevation={0} sx={{ ...SX.card, p: 2 }}>
            <Typography component='h2' sx={{ ...TYPE.sectionTitle, mb: 0.5 }}>Merchants on the {courier} TAT</Typography>
            {usedBy.length ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                {usedBy.map((n) => <Box key={n} component='span' sx={{ ...SX.pill, color: C.inkBody, bgcolor: C.surfaceAlt, border: `1px solid ${C.line}` }}>{n}</Box>)}
              </Box>
            ) : (
              <Typography sx={TYPE.rowHelp}>None yet. Put a merchant on it from onboarding Step 2 (TAT) or Merchant Update.</Typography>
            )}
          </Card>
          {grid.courierLanes ? (
            <Card elevation={0} sx={{ ...SX.card, p: 2 }}>
              <Typography component='h2' sx={{ ...TYPE.sectionTitle, mb: 1.25 }}>{courier} lanes against the standard</Typography>
              <CustomLanes grid={grid} source='P' valueLabel={courier} />
            </Card>
          ) : (
            <EmptyState dense title={`No ${courier} TAT yet`}
              body='Download the starting file (the standard lanes), keep only the lanes this courier gives a TAT for, set its MIN and MAX, and upload it.'
              action={<Button variant='contained' startIcon={<UploadFileOutlinedIcon />} onClick={() => navigate(ROUTES.tatCourierUpload(courier))} sx={SX.cta}>Upload TAT</Button>} />
          )}
          <Card elevation={0} sx={{ ...SX.card, p: 2 }}>
            <Typography component='h2' sx={{ ...TYPE.sectionTitle, mb: 1.25 }}>Effective TAT by lane</Typography>
            <TatMatrix grid={grid} showSource />
          </Card>
        </Box>
      )}
      {PolicyDialog}
    </PtlPage>
  )
}
